import { Form, Link, useLoaderData, useSubmit } from '@remix-run/react'
import {
	Menu,
	LogOut,
	Download,
	FileText,
	Users,
	Settings,
	Shield,
	User,
	MessageCircle,
	Instagram,
} from 'lucide-react'
import { useRef } from 'react'
import { getUserImgSrc, isUserAdmin } from '../../utils/misc.tsx'
import { useOptionalUser } from '../../utils/user.ts'
import { Button } from '../ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuPortal,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuGroup,
	DropdownMenuLabel,
} from '../ui/dropdown-menu.tsx'

export function RightDropdown() {
	const user = useOptionalUser()
	const submit = useSubmit()
	const { impersonator } = useLoaderData<{ impersonator?: boolean }>()
	const formRef = useRef<HTMLFormElement>(null)

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					data-testid="menu-button"
					className="flex h-10 w-10 items-center justify-center rounded-full bg-iagtGold p-0 hover:bg-iagtGold/90"
				>
					<Menu className="h-5 w-5 text-iagtDarkBlue" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent
					sideOffset={8}
					align="end"
					className="max-h-[calc(100vh-80px)] w-[calc(100vw-24px)] overflow-y-auto rounded-xl sm:w-56"
				>
					{user ? (
						<>
							<DropdownMenuItem asChild className="py-3">
								<Link
									prefetch="viewport"
									to={`/users/${user.username}`}
									className="flex items-center gap-2"
								>
									<img
										className="h-6 w-6 rounded-full object-cover object-top"
										alt={user.firstName ?? user.username}
										src={getUserImgSrc(user.image?.id)}
									/>
									<span className="truncate">
										{user.firstName ?? user.username}
									</span>
								</Link>
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuLabel className="text-sm font-semibold text-muted-foreground">
								Actions
							</DropdownMenuLabel>
							<DropdownMenuGroup>
								<DropdownMenuItem asChild className="py-3">
									<Link
										prefetch="intent"
										to="/settings/profile"
										className="flex items-center gap-2"
									>
										<Settings className="h-5 w-5" />
										<span>Update Profile</span>
									</Link>
								</DropdownMenuItem>
								{isUserAdmin(user) && (
									<DropdownMenuItem asChild className="py-3">
										<Link
											prefetch="intent"
											to="/admin/dashboard"
											className="flex items-center gap-2"
										>
											<Shield className="h-5 w-5" />
											<span>Admin Panel</span>
										</Link>
									</DropdownMenuItem>
								)}
							</DropdownMenuGroup>
							{impersonator && (
								<>
									<DropdownMenuSeparator />
									<DropdownMenuItem asChild className="py-3">
										<Form action="/impersonate" method="POST">
											<input type="hidden" name="intent" value="stop" />
											<Button
												variant="ghost"
												className="flex w-full items-center gap-2"
											>
												<LogOut className="h-5 w-5" />
												<span>
													Stop Impersonating {user.firstName} {user.lastName}
												</span>
											</Button>
										</Form>
									</DropdownMenuItem>
								</>
							)}
						</>
					) : (
						<DropdownMenuItem asChild className="py-3">
							<Link
								prefetch="intent"
								to="/me"
								className="flex items-center gap-2"
							>
								<User className="h-5 w-5" />
								<span>Log in</span>
							</Link>
						</DropdownMenuItem>
					)}

					<DropdownMenuSeparator />
					<DropdownMenuLabel className="text-sm font-semibold text-muted-foreground">
						Resources
					</DropdownMenuLabel>
					<DropdownMenuGroup>
						<DropdownMenuItem asChild className="py-3">
							<Link
								prefetch="viewport"
								to="/documents"
								className="flex items-center gap-2"
							>
								<FileText className="h-5 w-5" />
								<span>Documents</span>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild className="py-3">
							<Link
								prefetch="viewport"
								to="/organizers"
								className="flex items-center gap-2"
							>
								<Users className="h-5 w-5" />
								<span>Organizers</span>
							</Link>
						</DropdownMenuItem>
					</DropdownMenuGroup>

					<DropdownMenuSeparator />
					<DropdownMenuLabel className="text-sm font-semibold text-muted-foreground">
						Support
					</DropdownMenuLabel>
					<DropdownMenuGroup>
						<DropdownMenuItem asChild className="py-3">
							<Link
								prefetch="viewport"
								to="/install-app"
								className="flex items-center gap-2"
							>
								<Download className="h-5 w-5" />
								<span>Install App</span>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild className="py-3">
							<Link
								prefetch="viewport"
								to="/help"
								className="flex items-center gap-2"
							>
								<MessageCircle className="h-5 w-5" />
								<span>Contact Support</span>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild className="py-3">
							<a
								href="https://dub.sh/iagt-insta"
								target="_blank"
								rel="noreferrer"
								className="flex items-center gap-2"
							>
								<Instagram className="h-5 w-5" />
								<span>Follow on Instagram</span>
							</a>
						</DropdownMenuItem>
					</DropdownMenuGroup>

					{user && (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								asChild
								className="py-3"
								onSelect={event => {
									event.preventDefault()
									submit(formRef.current)
								}}
							>
								<Form action="/logout" method="POST" ref={formRef}>
									<Button
										variant="ghost"
										className="flex w-full items-center gap-2 text-destructive"
									>
										<LogOut className="h-5 w-5" />
										<span>Logout</span>
									</Button>
								</Form>
							</DropdownMenuItem>
						</>
					)}
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
